import { useTranslation } from 'react-i18next';
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography, alpha } from '@mui/material';
import {
  ArrowDownwardOutlined as DownIcon,
  ArrowUpwardOutlined as UpIcon,
  Close as ClearIcon,
  LocationOnOutlined as StopsIcon,
  MapOutlined as DistanceIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import LabelChips from '../common/LabelChips';
import type { Route } from '../../types/route.type';
import { getRouteColor } from '../../utils/routeColors';
import {
  buildRouteTimeline,
  resolveMismatchedAssignedTruck,
  type ScheduleCollectionPointSummary,
} from './truckRouteScheduleTab.helpers';

export interface ScheduledRouteCardProps {
  route: Route;
  /** The truck whose weekly schedule tab this card is rendered on. */
  viewerTruckId: string;
  canEdit: boolean;
  /** Removes just this route from its day -- the day itself may keep other routes. */
  onClear: () => void;
  onViewRoute: () => void;
  /** This route's collection points, already resolved and in route order. */
  collectionPoints: ScheduleCollectionPointSummary[];
  /** True while the org-wide collection-points fetch (shared across every day's card) is in flight. */
  collectionPointsLoading: boolean;
  /**
   * Reorder handlers for this card's position within its day's route list
   * (Task 3: 2+ ordered routes per weekday) -- omitted entirely by the
   * caller (`TruckRouteScheduleTab.tsx`'s `DayCell`) for a day with only one
   * route, since reordering a single-item list is meaningless. Mirrors the
   * arrow-reorder idiom in `pages/transportation/RouteBuilder.tsx`'s
   * `movePoint` (disabled at the ends, no drag-and-drop).
   */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

/**
 * Presentational content for a filled `DayCell` in `TruckRouteScheduleTab.tsx`'s
 * weekly calendar table. Everything rendered here already lives on `route` (`entry.route`)
 * -- no extra fetches -- this is purely a richer read of fields the API was
 * already sending: stop/distance stats, the route's facility flow, its zone
 * labels, and a note for when the route is also assigned (elsewhere, e.g. for
 * ownership purposes) to a *different* truck than the one whose week is on
 * screen (see `resolveMismatchedAssignedTruck`). A `DayCell` now renders one
 * of these per route scheduled on that day (Task 3: 2+ ordered routes per
 * weekday), so this card also owns its own optional reorder controls (see
 * `onMoveUp`/`onMoveDown`) alongside the always-present view/clear actions.
 */
export default function ScheduledRouteCard({
  route,
  viewerTruckId,
  canEdit,
  onClear,
  onViewRoute,
  collectionPoints,
  collectionPointsLoading,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: ScheduledRouteCardProps) {
  const { t } = useTranslation();
  const showReorder = canEdit && (onMoveUp !== undefined || onMoveDown !== undefined);
  const routeColor = getRouteColor(route.id);
  const mismatchedTruck = resolveMismatchedAssignedTruck(route, viewerTruckId);
  const timeline = buildRouteTimeline(route.start_facility, collectionPoints, route.end_facility);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Stack direction="row" spacing={0.75} alignItems="flex-start">
        <Box sx={{ width: 10, height: 10, mt: 0.5, borderRadius: '50%', bgcolor: routeColor, flexShrink: 0 }} />
        <Typography
          variant="body2"
          fontWeight={600}
          title={route.name}
          sx={{
            flex: 1,
            minWidth: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {route.name}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
          <StopsIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">
            {t('transportation.fleet.details.schedule.stopCount', { count: route.stop_count })}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
          <DistanceIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">{`${route.estimated_distance_km} km`}</Typography>
        </Stack>
      </Stack>

      <LabelChips labels={route.labels} />

      {collectionPointsLoading ? (
        <Skeleton variant="rounded" height={96} />
      ) : timeline.length > 0 ? (
        <Box sx={{ maxHeight: 200, overflowY: 'auto', py: 0.5 }}>
          <Stack>
            {timeline.map((node, index) => {
              const isLast = index === timeline.length - 1;
              return (
                <Stack key={node.id} direction="row" spacing={1} alignItems="stretch">
                  <Stack alignItems="center" sx={{ width: 14, flexShrink: 0 }}>
                    <Box sx={{ width: 8, height: 8, mt: '3px', borderRadius: '50%', bgcolor: routeColor, flexShrink: 0 }} />
                    {!isLast ? (
                      <Box sx={{ width: '2px', flex: 1, my: '2px', bgcolor: alpha(routeColor, 0.3) }} />
                    ) : null}
                  </Stack>
                  <Box sx={{ minWidth: 0, pb: isLast ? 0 : 0.75 }}>
                    <Typography variant="caption" noWrap title={node.name} sx={{ display: 'block' }}>
                      {node.name}
                    </Typography>
                    {node.supplierName ? (
                      <Typography
                        variant="caption"
                        noWrap
                        title={node.supplierName}
                        sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary', opacity: 0.7 }}
                      >
                        {node.supplierName}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      {mismatchedTruck ? (
        <Typography variant="caption" color="text.secondary" fontStyle="italic">
          {t('transportation.fleet.details.schedule.alsoAssignedTo', { truckName: mismatchedTruck.name })}
        </Typography>
      ) : null}

      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        <IconButton
          size="small"
          aria-label={t('transportation.fleet.details.schedule.viewRouteDetails', { routeName: route.name })}
          onClick={onViewRoute}
        >
          <ViewIcon fontSize="small" />
        </IconButton>
        {showReorder ? (
          <>
            <Tooltip title={t('transportation.fleet.details.schedule.moveRouteUp')}>
              <span>
                <IconButton size="small" disabled={!canMoveUp} onClick={onMoveUp}>
                  <UpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('transportation.fleet.details.schedule.moveRouteDown')}>
              <span>
                <IconButton size="small" disabled={!canMoveDown} onClick={onMoveDown}>
                  <DownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : null}
        {canEdit ? (
          <IconButton
            size="small"
            aria-label={t('transportation.fleet.details.schedule.clearRoute')}
            onClick={onClear}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    </Box>
  );
}
